import { Button, InfoIcon, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { InfoBox } from "../../InfoBox/InfoBox";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { accountOptions } from "./linkAccountOptions";
import css from "../orgList.module.scss";

const LinkAccountModal_User = graphql(`
  fragment LinkAccountModal_User on User {
    uuid
    email
    profiles {
      uuid
      organization {
        uuid
        name
      }
    }
    profilesConnection {
      totalCount
    }
  }
`);

const LinkAccountModal_Query = graphql(`
  query LinkAccountModal_Query {
    users {
      ...LinkAccountModal_User
    }
  }
`);

const LinkAccountModal_LinkAccount = graphql(`
  mutation LinkAccountModal_LinkAccount($where: ProfileWhere, $update: ProfileUpdateInput) {
    updateProfiles(where: $where, update: $update) {
      profiles {
        uuid
        user {
          uuid
        }
      }
    }
  }
`);

export type LinkAccountProfile = {
  uuid: string;
  /** Display name for the footer hint and the success toast. */
  name: string;
  org: { uuid: string; name: string };
};

type Props = {
  /** The account-less profile the account gets attached to. */
  profile: LinkAccountProfile;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * Connects an existing login account (User) to an existing profile that has none.
 * The select lists every account; accounts that already carry profiles show them
 * as context. The account keeps its other profiles — this only adds the link.
 */
export function LinkAccountModal({ profile, onClose, onLinked }: Props) {
  const [accountUuid, setAccountUuid] = useState("");
  const [busy, setBusy] = useState(false);
  const [{ data, fetching }] = useQuery({ query: LinkAccountModal_Query });
  const [, linkAccount] = useMutation(LinkAccountModal_LinkAccount);
  const accounts = getFragmentData(LinkAccountModal_User, data?.users ?? []);
  const options = accountOptions(accounts);

  const link = async () => {
    if (!accountUuid) {
      return;
    }
    setBusy(true);
    const res = await linkAccount({
      where: { uuid: profile.uuid },
      update: { user: { connect: { where: { node: { uuid: accountUuid } } } } },
    });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Verknüpfen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`Konto mit „${profile.name}" verknüpft`);
    onLinked();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title="Konto verknüpfen"
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>
            „{profile.name}" in {profile.org.name} hängt danach an diesem Konto.
          </span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              hierarchy="primary"
              disabled={!accountUuid || busy || fetching}
              onClick={() => void link()}
            >
              Verknüpfen
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Select
          name="account"
          label="Bestehendes Konto"
          placeholder="Konto suchen …"
          options={options}
          value={options.find((o) => o.value === accountUuid) ?? null}
          isSearchable
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) => {
            const value = (opt as SingleValue<SelectOptionType>)?.value;
            setAccountUuid(typeof value === "string" ? value : "");
          }}
        />
        <InfoBox tone="primary" icon={<InfoIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            Das Profil erhält Login, Einladungen und Passwort-Reset über dieses Konto. Bestehende
            Profile des Kontos bleiben unverändert.
          </span>
        </InfoBox>
      </div>
    </Modal>
  );
}
